if ( typeof CSSSlider !== 'function' ) {

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const hasScrollEnd = 'onscrollend' in window;

  // Same-value writes still count as mutations, which wake style invalidation and page-wide observers.
  const setAttr = (el, name, value) => {
    if ( value === null ) { if ( el.hasAttribute(name) ) el.removeAttribute(name); }
    else if ( el.getAttribute(name) !== String(value) ) el.setAttribute(name, value);
  };
  const setText = (el, value) => {
    if ( el && el.textContent !== String(value) ) el.textContent = value;
  };

  // Phased so a batch of sliders shares a couple of layouts instead of forcing a few each.
  const resetSliders = batch => {
    batch.forEach(([slider]) => slider._measureSlides());
    batch.forEach(([slider, opts]) => slider._applyPages(opts.resetIndex));
    batch.forEach(([slider]) => slider._measureMargins());
    batch.forEach(([slider]) => slider._measurePages());
    batch.forEach(([slider, opts]) => { if ( ! opts.nojump ) slider._jump(); });
    const inert = batch.map(([slider]) => slider._target == null ? slider._inertState() : null);
    batch.forEach(([slider, opts], i) => slider._finishReset(opts.init, inert[i]));
    batch.forEach(([slider, opts]) => slider._announceReset(opts.init));
  };

  const pending = new Map();
  let flushQueued = false;
  const queueReset = (slider, opts) => {
    const prev = pending.get(slider);
    pending.set(slider, prev ? {
      nojump: prev.nojump && opts.nojump,
      resetIndex: prev.resetIndex || opts.resetIndex,
      init: prev.init || opts.init
    } : opts);
    if ( flushQueued ) return;
    flushQueued = true;
    queueMicrotask(() => {
      flushQueued = false;
      const batch = [...pending].filter(([slider]) => slider.isConnected);
      pending.clear();
      resetSliders(batch);
    });
  };

  const holders = new WeakMap();
  const resizeObserver = new ResizeObserver(entries => {
    const batch = [];
    entries.forEach(({ target }) => {
      const slider = holders.get(target);
      if ( ! slider ) return;
      const width = target.clientWidth;
      if ( width === slider._width ) return;
      slider._width = width;
      if ( width ) batch.push([slider, { nojump: false, resetIndex: false }]);
    });
    resetSliders(batch);
  });

  class CSSSlider extends HTMLElement {

    constructor(){
      super();
      this._prefix = window.KT_PREFIX || '';
      this._touchScreen = document.body.classList.contains('touchevents');
      this._rtl = document.documentElement.getAttribute('dir') == 'rtl';

      const p = this._prefix;
      this.o = {
        domReady: false,
        selector: `.${p}css-slide`,
        groupCells: false,
        includeGapInGrouping: false,
        forceOneSlidePerPage: false,
        navigation: true,
        navigationDOM: `<span class="${p}css-slider-button ${p}css-slider-prev" style="display:none">←</span>
          <span class="${p}css-slider-button ${p}css-slider-next" style="display:none">→</span>`,
        thumbnails: true,
        thumbnailsDOM: `<div class="${p}css-slider-dot-navigation" style="display:none"></div>`,
        thumbnailsNoInteraction: false,
        indexNav: false,
        indexNavDOM: `<div class="${p}css-slider-index-navigation"><span class="${p}css-slider-current">1</span> / <span class="${p}css-slider-total">1</span></div>`,
        watchCSS: false,
        disableSwipe: false,
        disableMouseDownEvent: true,
        disableTabNavigation: false,
        autoplay: 0,
        forceFirstSlide: false,
        sliderWidthSelector: null,
        ...JSON.parse(this.dataset.options || '{}')
      };

      this.index = 0;
      this.length = 0;
      this.items = [];
      this.indexedItems = [];
      this.sliderEnabled = false;
    }

    connectedCallback(){
      if ( this.o.watchCSS ) {
        this._watchAbort = new AbortController();
        const watch = debounce(() => {
          const wanted = getComputedStyle(this, ':after').content.includes('css-slide');
          const live = this.sliderEnabled || this._initPending;
          if ( wanted && ! live ) this.initSlider();
          else if ( ! wanted && live ) this.destroySlider();
        }, 100);
        window.addEventListener('resize', watch, { signal: this._watchAbort.signal });
        watch();
      }

      if ( this._initPending ) {
        this._bind();
        queueReset(this, this._initPending);
      } else if ( this.sliderEnabled ) {
        this._bind();
        queueReset(this, { nojump: true, resetIndex: false });
      } else if ( ! this.o.watchCSS ) {
        this.initSlider();
      }
    }

    disconnectedCallback(){
      this._watchAbort?.abort();
      this._unbind();
      pending.delete(this);
    }

    initSlider(){
      const p = this._prefix, o = this.o;

      this.classList.add(`${p}css-slider`);
      if ( ! o.domReady ) {
        this.originalHTML = this.innerHTML;
        this.innerHTML = `<div class="${p}css-slider-viewport"><div class="${p}css-slider-holder"${o.disableTabNavigation ? ' tabindex="-1"' : ''}><div class="${p}css-slider-container">${this.originalHTML}</div></div></div>`;
      }

      if ( o.selector != `.${p}css-slide` ) {
        this.querySelectorAll(o.selector).forEach(elm => elm.classList.add(`${p}css-slide`));
      }

      this.element = this.querySelector(`.${p}css-slider-holder`);
      this.viewport = this.querySelector(`.${p}css-slider-viewport`);
      this.items = this.querySelectorAll(o.selector);
      this.index = 0;

      this._buildNavigation();
      this._bind();
      this._initPending = { nojump: ! o.groupCells, resetIndex: true, init: true };
      queueReset(this, this._initPending);
    }

    destroySlider(){
      this._unbind();
      pending.delete(this);
      this._initPending = null;
      if ( this.originalHTML !== undefined ) this.innerHTML = this.originalHTML;
      this.classList.remove(`${this._prefix}enabled`);
      this.sliderEnabled = false;
      this.dispatchEvent(new CustomEvent('destroy'));
    }

    afterAppend(){
      this.items = this.querySelectorAll(this.o.selector);
    }

    changeSlide(direction, behavior = 'smooth'){
      if ( direction == 'next' ) {
        if ( this.index + 1 < this.length ) this.index++;
      } else if ( direction == 'prev' ) {
        if ( this.index > 0 ) this.index--;
      } else if ( parseInt(direction) >= 0 ) {
        this.index = Math.min(parseInt(direction), Math.max(this.length - 1, 0));
      }

      this._measurePages();
      const left = this._pageOffsets[this.index] || 0;

      if ( Math.abs(Math.abs(this.element.scrollLeft) - left) < 1 ) {
        this._target = null;
        this._updateInert();
      } else {
        this._target = this.index;
        this.element.scrollTo({
          top: 0,
          left: this._rtl ? -left : left,
          behavior: behavior == 'smooth' && reducedMotion.matches ? 'instant' : behavior
        });
      }

      this._changed();
    }

    checkSlide(){
      this._render();
      if ( this._target == null ) this._updateInert();
    }

    resetSlider(nojump = false, resetIndex = true){
      resetSliders([[this, { nojump, resetIndex }]]);
    }

    static resetAll(sliders, nojump = false, resetIndex = true){
      resetSliders(Array.from(sliders, slider => [slider, { nojump, resetIndex }]));
    }

    _measureSlides(){
      const p = this._prefix, o = this.o;
      this._width = this.element.clientWidth;
      const totalWidth = ((o.sliderWidthSelector && document.querySelector(o.sliderWidthSelector)) || this.querySelector(`.${p}css-slider-container`)).offsetWidth;
      const gap = o.includeGapInGrouping && this.items[0] ? parseFloat(getComputedStyle(this.items[0].parentElement).columnGap) || 0 : 0;

      let slidesWidth = 0, perPage = 0;
      this.items.forEach((elm, i) => {
        slidesWidth += elm.getBoundingClientRect().width + (i > 0 ? gap : 0);
        // Slack for a rounded offsetWidth against subpixel slides; an overflowing first slide is still one per page.
        if ( slidesWidth > totalWidth + 1 && ! perPage ) perPage = Math.max(i, 1);
      });

      this._fits = ! perPage || this.items.length <= 1;
      this.slidesPerPage = o.forceOneSlidePerPage ? 1 : perPage || this.items.length || 1;
    }

    _applyPages(resetIndex){
      const p = this._prefix;
      let page = 0;
      this.items.forEach((elm, i) => {
        const snap = i % this.slidesPerPage == 0;
        elm.classList.toggle(`${p}css-slide--snap`, snap);
        if ( snap ) elm.setAttribute('data-index', page++);
      });
      this.indexedItems = Array.from(this.items).filter((elm, i) => i % this.slidesPerPage == 0);
      this._pageObserver?.disconnect();
      this.indexedItems.forEach(elm => this._pageObserver?.observe(elm));
      this._skipFirstObservation = !! this._pageObserver && this.indexedItems.length > 0;

      this.length = Math.max(Math.ceil(this.items.length / this.slidesPerPage), 1);
      this.index = resetIndex ? 0 : Math.min(this.index, this.length - 1);

      this._buildThumbnails();
      setText(this.lengthEl, this.length);

      this.element.classList.toggle(`${p}css-slider--no-drag`, this._fits);
      this._toggleNavigation(! this._fits);
      this.setAttribute('data-slider-length', this.length);
    }

    _measureMargins(){
      this._margins = this.indexedItems.map(elm => {
        const style = getComputedStyle(elm);
        return {
          align: style.scrollSnapAlign.split(' ').pop(),
          start: parseFloat(style.scrollMarginInlineStart) || 0,
          end: parseFloat(style.scrollMarginInlineEnd) || 0
        };
      });
    }

    _jump(){
      const left = this._pageOffsets[this.index] || 0;
      this.element.scrollTo({ top: 0, left: this._rtl ? -left : left, behavior: 'instant' });
    }

    _finishReset(init, inert){
      if ( inert ) this._applyInert(inert);
      this._render();
      if ( ! init ) return;
      this._initPending = null;
      this.classList.add(`${this._prefix}enabled`);
      this.sliderEnabled = true;
    }

    _announceReset(init){
      this.dispatchEvent(new CustomEvent('reset'));
      if ( ! init ) return;
      this.dispatchEvent(new CustomEvent('ready'));
      this._startAutoplay();
      if ( this.o.forceFirstSlide && ! this._forcedSlideChange ) {
        this._forcedSlideChange = true;
        setTimeout(() => this.changeSlide(0), 10);
      }
    }

    _bind(){
      this._unbind();
      this._abort = new AbortController();
      const p = this._prefix, o = this.o, el = this.element, signal = this._abort.signal;

      if ( ! hasScrollEnd ) el.addEventListener('scroll', () => this._onScroll(), { passive: true, signal });
      el.addEventListener('scrollend', () => this._settle(), { signal });
      el.addEventListener('click', e => this._onClickReveal(e), { capture: true, signal });
      el.addEventListener('focusout', () => {
        if ( this._target == null ) requestAnimationFrame(() => this._updateInert());
      }, { signal });

      if ( o.disableMouseDownEvent ) {
        this.querySelector(`.${p}css-slider-container`).addEventListener('mousedown', e => {
          if ( e.target.tagName !== 'SELECT' ) e.preventDefault();
        }, { signal });
      }

      if ( ! this._touchScreen ) {
        el.addEventListener('dragstart', e => e.preventDefault(), { signal });
      }

      if ( ! o.disableSwipe && ! this._touchScreen ) {
        let startX = null;
        const release = () => {
          startX = null;
          el.classList.remove(`${p}mouse-down`, `${p}pointer-events-off`);
        };
        el.addEventListener('mousedown', e => {
          if ( el.classList.contains(`${p}css-slider--disable-dragging`) || el.classList.contains(`${p}css-slider--no-drag`) ) return;
          // A drag inside a nested slider that can scroll belongs to that slider alone.
          const holder = e.target.closest(`.${p}css-slider-holder`);
          if ( holder !== el && holder.scrollWidth > holder.clientWidth ) return;
          startX = e.screenX;
          el.classList.add(`${p}mouse-down`);
        }, { signal });
        el.addEventListener('mousemove', e => {
          if ( startX === null ) return;
          const dx = startX - e.screenX;
          if ( Math.abs(dx) <= 1 ) return;
          el.classList.add(`${p}pointer-events-off`);
          this.changeSlide((dx > 0) != this._rtl ? 'next' : 'prev');
          startX = null;
        }, { signal });
        el.addEventListener('mouseup', release, { signal });
        el.addEventListener('mouseleave', release, { signal });
      }

      if ( parseInt(o.autoplay) > 0 ) {
        const pause = () => { this._paused = true; this._stopAutoplay(); };
        const resume = () => { this._paused = false; this._startAutoplay(); };
        this.addEventListener('pointerenter', pause, { signal });
        this.addEventListener('pointerleave', resume, { signal });
        this.addEventListener('focusin', pause, { signal });
        this.addEventListener('focusout', e => { if ( ! this.contains(e.relatedTarget) ) resume(); }, { signal });
      }

      holders.set(el, this);
      resizeObserver.observe(el);

      // Only a movement signal: a peeking slide can be over half visible, so the index comes from scroll position.
      this._pageObserver = new IntersectionObserver(() => {
        // The first callback after observe() only reports the state a reset just measured.
        if ( this._skipFirstObservation ) this._skipFirstObservation = false;
        else if ( this._target == null ) this._syncIndex();
      }, { root: el, threshold: [0, .5, 1] });
    }

    _unbind(){
      this._abort?.abort();
      if ( this.element ) resizeObserver.unobserve(this.element);
      this._pageObserver?.disconnect();
      clearTimeout(this._settleTimer);
      this._settleTimer = null;
      this._stopAutoplay();
    }

    _onScroll(){
      this._lastScroll = performance.now();
      if ( ! this._settleTimer ) this._waitForSettle(150);
    }

    _waitForSettle(delay){
      this._settleTimer = setTimeout(() => {
        const idle = performance.now() - this._lastScroll;
        if ( idle >= 150 ) this._settle();
        else this._waitForSettle(150 - idle);
      }, delay);
    }

    _settle(){
      clearTimeout(this._settleTimer);
      this._settleTimer = null;
      this._target = null;
      this._measurePages();
      this._syncIndex();
      this._updateInert();
    }

    _syncIndex(){
      const offsets = this._pageOffsets;
      if ( ! offsets?.length ) return;
      const x = Math.abs(this.element.scrollLeft);
      let nearest = 0;
      // <= so pages that share the end offset resolve to the last one.
      offsets.forEach((offset, i) => {
        if ( Math.abs(offset - x) <= Math.abs(offsets[nearest] - x) ) nearest = i;
      });
      if ( nearest === this.index ) return;
      this.index = nearest;
      this._changed();
    }

    _measurePages(){
      const el = this.element, box = el.getBoundingClientRect();
      const scrolled = Math.abs(el.scrollLeft), max = Math.max(el.scrollWidth - el.clientWidth, 0);
      const last = this.indexedItems.length - 1;
      this._pageOffsets = this.indexedItems.map((elm, i) => {
        if ( i === 0 ) return 0;
        if ( i === last ) return max;
        const rect = elm.getBoundingClientRect();
        const start = (this._rtl ? box.right - rect.right : rect.left - box.left) + scrolled;
        const m = this._margins[i] || { start: 0, end: 0 };
        let offset = start - m.start;
        if ( m.align == 'center' ) offset = start + (rect.width + m.end - m.start - el.clientWidth) / 2;
        else if ( m.align == 'end' ) offset = start + rect.width + m.end - el.clientWidth;
        return Math.min(Math.max(offset, 0), max);
      });
    }

    _changed(){
      this._render();
      this.dispatchEvent(new CustomEvent('change'));
      if ( this._autoplayTimer ) this._startAutoplay();
    }

    _render(){
      const p = this._prefix;
      if ( this.prevEl ) this._setDisabled(this.prevEl, this.index <= 0);
      if ( this.nextEl ) this._setDisabled(this.nextEl, this.index >= this.length - 1);
      this.thumbnails?.forEach((dot, i) => {
        dot.classList.toggle(`${p}active`, i == this.index);
        setAttr(dot, 'aria-current', i == this.index ? 'true' : null);
      });
      setText(this.indexEl, this.index + 1);
    }

    _setDisabled(button, disabled){
      button.classList.toggle(`${this._prefix}disabled`, disabled);
      setAttr(button, 'aria-disabled', disabled);
    }

    _updateInert(){
      const inert = this._inertState();
      if ( inert ) this._applyInert(inert);
    }

    _inertState(){
      if ( this.o.disableTabNavigation || ! this.viewport ) return null;
      const view = this.viewport.getBoundingClientRect();
      if ( ! view.width ) return null;
      const focused = document.activeElement;
      return Array.from(this.items, elm => {
        const rect = elm.getBoundingClientRect();
        const visible = rect.width > 0 && rect.right > view.left + 1 && rect.left < view.right - 1;
        // Inerting the focused slide would drop focus to <body>.
        return ! visible && ! elm.contains(focused);
      });
    }

    _applyInert(inert){
      this.items.forEach((elm, i) => {
        if ( elm.inert !== inert[i] ) elm.inert = inert[i];
      });
    }

    _onClickReveal(e){
      // Spend the first press on a partly hidden slide bringing it into view, and leave activation
      // to the press that follows. Keyboard and scripted activation (detail 0) already scrolled it.
      if ( e.detail === 0 || this.length < 2 ) return;

      const slide = e.target.closest(`.${this._prefix}css-slide`);
      // Nested sliders handle their own slides against the track that actually clips them.
      if ( ! slide || slide.closest('css-slider') !== this ) return;
      if ( ! e.target.closest('a, button, summary, label, input, select, textarea, [role="button"]') ) return;

      const trackRect = this.element.getBoundingClientRect(),
            slideRect = slide.getBoundingClientRect();

      // Never fully revealable, so the press would be swallowed for good.
      if ( slideRect.width > trackRect.width + 1 ) return;

      // Slack for the fractional widths of slides sized to tile the track.
      const shown = Math.min(slideRect.right, trackRect.right) - Math.max(slideRect.left, trackRect.left);
      if ( shown >= slideRect.width - 1 ) return;

      const slideIndex = Array.from(this.items).indexOf(slide);
      if ( slideIndex < 0 ) return;

      e.preventDefault();
      e.stopPropagation();
      this.changeSlide(Math.floor(slideIndex / (this.slidesPerPage || 1)));
    }

    _buildNavigation(){
      const p = this._prefix, o = this.o;
      this.prevEl = this.nextEl = this.thumbnailsEl = this.indexEl = this.lengthEl = null;
      if ( ! o.navigation && ! o.thumbnails && ! o.indexNav ) return;

      const arrows = new DOMParser().parseFromString(o.navigationDOM, 'text/html');
      const container = document.createElement('div');
      container.classList.add(`${p}css-slider-navigation-container`);
      container.innerHTML = `
        ${o.navigation ? arrows.querySelector(`.${p}css-slider-prev`).outerHTML : ''}
        ${o.thumbnails ? o.thumbnailsDOM : ''}
        ${o.navigation ? arrows.querySelector(`.${p}css-slider-next`).outerHTML : ''}
        ${o.indexNav ? o.indexNavDOM : ''}`;

      const activate = e => {
        if ( e.key !== 'Enter' && e.key !== ' ' ) return;
        e.preventDefault();
        e.target.click();
      };

      if ( o.navigation ) {
        const locales = window.KROWN.settings.locales;
        [['prevEl', 'prev', locales.slider_prev_button_label], ['nextEl', 'next', locales.slider_next_button_label]].forEach(([key, direction, label]) => {
          const button = this[key] = container.querySelector(`.${p}css-slider-${direction}`);
          button.setAttribute('tabindex', 0);
          button.setAttribute('role', 'button');
          button.querySelector('svg')?.setAttribute('aria-hidden', 'true');
          const text = document.createElement('span');
          text.textContent = label;
          text.classList.add(`${p}visually-hidden`);
          button.appendChild(text);
          button.addEventListener('click', e => {
            e.preventDefault();
            this.changeSlide(direction);
            this.dispatchEvent(new CustomEvent('navigation'));
          });
          button.addEventListener('keydown', activate);
        });
      }

      if ( o.thumbnails ) {
        this.thumbnailsEl = container.querySelector(`.${p}css-slider-dot-navigation`);
        if ( ! o.thumbnailsNoInteraction ) {
          this.thumbnailsEl.addEventListener('click', e => {
            const dot = e.target.closest('[data-index]');
            if ( dot ) this.changeSlide(dot.dataset.index);
          });
          this.thumbnailsEl.addEventListener('keydown', e => {
            if ( e.target.matches('[data-index]') ) activate(e);
          });
        }
      }

      if ( o.indexNav ) {
        this.indexEl = container.querySelector(`.${p}css-slider-current`);
        this.lengthEl = container.querySelector(`.${p}css-slider-total`);
      }

      this.append(container);
    }

    _buildThumbnails(){
      if ( ! this.thumbnailsEl || this.thumbnails?.length === this.length ) return;
      const p = this._prefix, label = window.KROWN.settings.locales.slider_thumbnail_label;
      const dots = [];
      for ( let i = 0; i < this.length; i++ ) {
        const dot = document.createElement('span');
        dot.classList.add(`${p}css-slider-dot`);
        dot.dataset.index = i;
        if ( ! this.o.thumbnailsNoInteraction ) {
          dot.setAttribute('aria-label', label.replace('{{ count }}', i + 1));
          dot.setAttribute('role', 'button');
          dot.setAttribute('tabindex', 0);
        }
        dots.push(dot);
      }
      this.thumbnailsEl.replaceChildren(...dots);
      this.thumbnailsEl.style.setProperty('--size', this.length);
      this.thumbnails = dots;
    }

    _toggleNavigation(show){
      const display = show ? 'block' : 'none';
      if ( this.prevEl ) this.prevEl.style.display = this.nextEl.style.display = display;
      if ( this.thumbnailsEl ) this.thumbnailsEl.style.display = display;
    }

    _startAutoplay(){
      const interval = parseInt(this.o.autoplay);
      if ( ! ( interval > 0 ) || this._paused || this.length < 2 ) return;
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = setInterval(() => {
        this.changeSlide(this.index + 1 >= this.length ? 0 : 'next');
      }, interval);
      const running = `${this._prefix}autoplay-running`;
      if ( ! this.classList.contains(running) ) {
        this.classList.add(running);
        return;
      }
      // Rewind the progress bar without the remove-class/reflow/add-class restart.
      this.getAnimations({ subtree: true }).forEach(animation => {
        if ( animation.animationName === 'animate-width' && animation.effect?.target?.closest?.('css-slider') === this ) animation.currentTime = 0;
      });
    }

    _stopAutoplay(){
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
      this.classList.remove(`${this._prefix}autoplay-running`);
    }

  }

  if ( typeof customElements.get('css-slider') == 'undefined' ) {
    customElements.define('css-slider', CSSSlider);
  }

  document.addEventListener('shopify:section:load', (e)=>{
    if ( e.target.classList.contains('mount-css-slider') ) {
      CSSSlider.resetAll([...e.target.querySelectorAll('css-slider')].filter(slider => slider.sliderEnabled));
    }
  });

}
